import Component from 'vue-class-component';
import { Mixins } from 'vue-property-decorator';
import {
    MagicConfigMixin,
    translationExists,
    queryTranslations,
} from '@gig/magic-common';
import { getMetaTags } from '@gig/magic-frontend-toolkit/src/utils/meta';
import { linktag } from '@gig/magic-frontend-toolkit/src/services/linktag';
import { meta, MetaTag } from '@gig/magic-frontend-toolkit/src/services/meta';
import { alternateLink } from '@gig/magic-frontend-toolkit/src/services/alternate-link';
/**
 * Adds meta information to the site for a particular page.
 */
@Component
export class MetaMixin extends Mixins(MagicConfigMixin) {
    private metaPage: string = 'default';
    private interpolationParams: Record<string, any> = {};
    urlSlug: string = '';

    setPage(
        page: string,
        options: {
            interpolationParams?: Record<string, any>;
            slug?: string;
        } = {},
    ) {
        const { interpolationParams = {}, slug = '' } = options;
        this.metaPage = page;
        this.interpolationParams = interpolationParams;
        this.urlSlug = slug;

        queryTranslations().then(() => {
            this.setMetaTags();
            this.setAlternateUrl();
        });
    }

    destroyed() {
        this.destroyAlternateUrl();
    }

    setMetaTags() {
        const page = this.metaPage;
        getMetaTags(page)
            .map((tag) => {
                if (this.urlSlug === '') {
                    return tag;
                }
                const key = tag.content.replace(
                    page,
                    `${page}.${this.urlSlug}`,
                );
                if (!this.$t(key)) {
                    return tag;
                }
                return { ...tag, content: key };
            })
            .forEach(({ name, content }) => {
                meta.setMetaTag({
                    name,
                    content: this.$t(content, this.interpolationParams),
                });
            });

        this.setPageTitle();
        this.setCanonicalUrl();
    }

    setPageDescription(description: string) {
        meta.setMetaTag({
            name: MetaTag.DESCRIPTION,
            content: description,
        });
    }

    setPageTitle(title?: string) {
        const titles = [
            `meta.${this.metaPage}.${this.urlSlug}.title`,
            `meta.${this.metaPage}.title`,
            `meta.default.title`,
        ];
        const pageTitle = titles.find(
            (key) => !!this.$t(key, this.interpolationParams),
        );

        if (title) {
            meta.setTitle(title);
        } else {
            meta.setTitle(this.$t(pageTitle, this.interpolationParams));
        }
    }

    private setCanonicalUrl() {
        const canonicalSlug = this.getPageCanonicalSlug();
        const pageCanonicalUrl = this.$t(
            canonicalSlug,
            this.interpolationParams,
        );

        // default canonical url is set on route change from `App` component
        // in case an explicit url is defined for the current page, we override it
        if (pageCanonicalUrl) {
            linktag.setCanonicalTagFromUrl(pageCanonicalUrl);
        }
    }

    private setAlternateUrl() {
        const hreflangSlug = this.getPageHreflangSlug();

        // default alternate url is set on route change from `App` component
        // in case an explicit url is defined for the current page, we override it
        if (!translationExists(hreflangSlug)) {
            return;
        }

        const alternateUrl = this.$t(hreflangSlug, this.interpolationParams);

        if (alternateUrl) {
            alternateLink.setAlternateTag(alternateUrl, {
                useBaseUrl: false,
            });
        } else {
            this.destroyAlternateUrl();
        }
    }

    private destroyAlternateUrl() {
        alternateLink.destroyAlternateTag();
    }

    private getPageHreflangSlug() {
        return `${this.getPageSlugPrefix()}.hreflang`;
    }

    private getPageCanonicalSlug() {
        return `${this.getPageSlugPrefix()}.canonical`;
    }

    private getPageSlugPrefix() {
        return this.urlSlug
            ? `meta.${this.metaPage}.${this.urlSlug}`
            : `meta.${this.metaPage}`;
    }
}
